import { z } from "zod";
const jsonObjectSchema = z.record(z.string(), z.unknown());
export const channelDataContractSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("source_view") }),
    z.object({
        kind: z.literal("twin_product"),
        publicationId: z.string().trim().min(1),
        publicationVersionId: z.string().trim().min(1),
        productSlug: z.string().trim().min(1),
        productVersion: z.number().int().positive(),
        contract: jsonObjectSchema,
        validation: jsonObjectSchema,
    }),
    // A current Twin Build product (a Twin Logic view contract), read at the version that serves it.
    z.object({
        kind: z.literal("view_contract"),
        productId: z.string().trim().min(1),
        productVersionId: z.string().trim().min(1),
        productSlug: z.string().trim().min(1),
        productVersion: z.number().int().positive(),
    }),
]);
export const channelDataSourceSchema = z.object({
    datasetId: z.string().trim().min(1),
    objectTypeId: z.string().trim().min(1).optional(),
    schemaName: z.string().trim().min(1),
    relationName: z.string().trim().min(1),
    sourceSystem: z.string().trim().min(1),
    dataContract: channelDataContractSchema,
    freshness: z.lazy(() => channelDataFreshnessSchema).optional(),
});
const nullableTimestampMsSchema = z.number().int().nonnegative().nullable();
export const channelFreshnessReasonCodeSchema = z.enum([
    "native_canonical",
    "native_unpopulated",
    "within_target",
    "approaching_target",
    "ingestion_active",
    "projection_active",
    "source_stale",
    "source_failed",
    "projection_failed",
    "projection_behind_source",
    "snapshot_current",
    "snapshot_expired",
    "lineage_unknown",
    "query_time_only",
    "watermark_unavailable",
]);
export const channelSourceWatermarkSchema = z.object({
    sourceSystem: z.string().trim().min(1),
    sourceReference: z.string().trim().min(1).optional(),
    sourceWatermark: nullableTimestampMsSchema,
    observedThrough: nullableTimestampMsSchema,
    lastIngestionAt: nullableTimestampMsSchema,
    nextScheduledAt: nullableTimestampMsSchema,
    nextRetryAt: nullableTimestampMsSchema,
    status: z.enum(["fresh", "aging", "refreshing", "stale", "failed", "unknown"]),
}).strict();
export const channelDataFreshnessSchema = z.object({
    kind: z.enum([
        "exact_connector",
        "external_mirror",
        "snapshot",
        "native",
        "query_only",
        "unknown",
    ]),
    status: z.enum(["fresh", "aging", "refreshing", "stale", "failed", "snapshot", "unknown"]),
    generatedAt: z.number().int().nonnegative(),
    dataAsOf: nullableTimestampMsSchema,
    ageMs: nullableTimestampMsSchema,
    maxStalenessMs: z.number().int().positive().nullable(),
    lastIngestionAt: nullableTimestampMsSchema,
    lastProjectionRefreshAt: nullableTimestampMsSchema,
    nextScheduledAt: nullableTimestampMsSchema,
    nextRetryAt: nullableTimestampMsSchema,
    snapshotCapturedAt: nullableTimestampMsSchema,
    snapshotExpiresAt: nullableTimestampMsSchema,
    desiredProjectionWatermark: nullableTimestampMsSchema,
    servedProjectionWatermark: nullableTimestampMsSchema,
    sourceSystems: z.array(z.string().trim().min(1)).max(20),
    sourceWatermarks: z.array(channelSourceWatermarkSchema).max(20),
    reasonCodes: z.array(channelFreshnessReasonCodeSchema).min(1).max(20),
    materialized: z.boolean(),
}).strict();
const channelConnectorFreshnessSourceSchema = z.object({
    integrationId: z.string().trim().min(1),
    entities: z.array(z.string().trim().min(1)).min(1).max(80),
}).strict();
export const channelDatasetFreshnessDeclarationSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("exact_connector"),
        sources: z.array(channelConnectorFreshnessSourceSchema).min(1).max(20),
        maxStalenessMs: z.number().int().positive().optional(),
    }).strict().superRefine((value, ctx) => {
        const integrationIds = value.sources.map((source) => source.integrationId);
        if (new Set(integrationIds).size === integrationIds.length)
            return;
        ctx.addIssue({
            code: "custom",
            path: ["sources"],
            message: "Exact connector freshness sources must use distinct integrations.",
        });
    }),
    z.object({
        kind: z.literal("snapshot"),
        capturedAt: z.number().int().nonnegative(),
        expiresAt: z.number().int().nonnegative().optional(),
    }).strict().superRefine((value, ctx) => {
        if (value.expiresAt === undefined || value.expiresAt >= value.capturedAt)
            return;
        ctx.addIssue({
            code: "custom",
            path: ["expiresAt"],
            message: "Snapshot expiry cannot precede capture time.",
        });
    }),
    z.object({
        kind: z.literal("external_mirror"),
        sourceSystem: z.string().trim().min(1).max(120),
        sourceReference: z.string().trim().min(1).max(2_000),
        dataAsOf: z.number().int().nonnegative(),
        maxStalenessMs: z.number().int().positive(),
    }).strict(),
    z.object({ kind: z.enum(["native", "query_only", "unknown"]) }).strict(),
]);
export const patchChannelDatasetFreshnessInputSchema = z.object({
    channelId: z.string().trim().min(1),
    objectTypeId: z.string().trim().min(1),
    freshness: channelDatasetFreshnessDeclarationSchema,
}).strict();
export const dashboardWidgetCalculationTraceSchema = z.object({
    widgetId: z.string().trim().min(1),
    widgetType: z.enum([
        "metric",
        "time_series",
        "breakdown",
        "bar",
        "grouped_bar",
        "stacked_bar",
        "waterfall",
        "record_list",
        "insight_list",
        "risk_list",
    ]),
    operation: z.enum([
        "aggregate",
        "bridge_aggregate",
        "time_bucket_aggregate",
        "category_aggregate",
        "segmented_category_aggregate",
        "segmented_time_aggregate",
        "record_selection",
        "snapshot",
        "derived_summary",
    ]),
    aggregate: z.enum(["count", "sum", "average", "min", "max"]).optional(),
    measureFieldKey: z.string().trim().min(1).optional(),
    dimensionFieldKey: z.string().trim().min(1).optional(),
    segmentFieldKey: z.string().trim().min(1).optional(),
    dateBucket: z.enum(["day", "week", "month", "year"]).optional(),
    completePeriodsOnly: z.boolean().optional(),
    variant: z.string().trim().min(1).optional(),
    limit: z.number().int().positive().optional(),
    sort: z.object({
        fieldKey: z.string().trim().min(1).optional(),
        direction: z.enum(["asc", "desc"]),
        mode: z.string().trim().min(1),
    }).optional(),
    columns: z.array(z.string().trim().min(1)).optional(),
    /** A waterfall's bars, each the sum of its field. */
    steps: z.array(z.object({
        fieldKey: z.string().trim().min(1),
        label: z.string().trim().min(1),
        kind: z.enum(["start", "change", "end"]),
    })).optional(),
    dateContext: z.object({
        fieldKey: z.string().trim().min(1),
        label: z.string().trim().min(1).optional(),
        periodStart: z.string().trim().min(1).optional(),
        periodEnd: z.string().trim().min(1).optional(),
    }).optional(),
    snapshot: z.object({
        capturedAt: z.number().int().nonnegative(),
        source: z.string().trim().min(1),
        sourceRef: z.string().trim().min(1).optional(),
    }).optional(),
    sourceWidgetIds: z.array(z.string().trim().min(1)).min(1).max(8).optional(),
});
